import * as Sentry from '@sentry/react';
import { AuditLog } from '@superfunnel/types';
import { ReactElement, createContext, useCallback, useContext } from 'react';
import { createAuditLog } from '../services/AuditLogService';
import { UserContext } from './UserContext';

export type CreateAuditLogFn = (
  action: AuditLog['action'],
  name: AuditLog['name'],
  entity?: AuditLog['entity'],
  entityId?: AuditLog['entityId'],
  entityName?: AuditLog['entityName'],
  changes?: AuditLog['changes'],
  context?: AuditLog['context'],
) => void;

interface AuditLogContextProps {
  log: CreateAuditLogFn;
}

export const AuditLogContext = createContext<AuditLogContextProps | null>(null);

export function AuditLogContextProvider({
  children,
}: {
  children: ReactElement<any>;
}) {
  const { selectedAccount } = useContext(UserContext);

  const addAuditLog: CreateAuditLogFn = useCallback(
    (action, name, entity, entityId, entityName, changes, context) => {
      if (!selectedAccount) return;

      createAuditLog(selectedAccount.id, {
        action,
        name,
        entity: entity || null,
        entityId: entityId || null,
        entityName: entityName || null,
        changes: changes ? JSON.parse(JSON.stringify(changes)) : null,
        context: context ? JSON.parse(JSON.stringify(context)) : null,
      }).catch((error) => {
        console.error('Error adding audit log: ', error);
        Sentry.captureException(error, {
          extra: {
            action,
            name,
            entity,
            entityId,
            entityName,
            changes,
            context,
          },
        });
      });
    },
    [selectedAccount],
  );

  return (
    <AuditLogContext.Provider
      value={{
        log: addAuditLog,
      }}
    >
      {children}
    </AuditLogContext.Provider>
  );
}

export function useAuditLog() {
  const context = useContext(AuditLogContext);
  if (!context) {
    throw new Error(
      'useAuditLog must be used within a AuditLogContextProvider',
    );
  }

  return context;
}
